import React, { useState, useEffect } from 'react';
import { Container, Typography, Box, Card, CardMedia, CardContent, Button, Dialog, DialogTitle, DialogContent, DialogActions, IconButton } from '@mui/material';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import MapIcon from '@mui/icons-material/Map';
import LocationOnIcon from '@mui/icons-material/LocationOn';

// Context
import { useI18n } from '../../context/I18nContext';

// Importando dados centralizados das cidades
import { City, getRandomCities } from '../../data/citiesData';

const SectionContainer = styled.section`
  padding: 5rem 0;
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: var(--border-radius);
  margin: 2rem 0;
`;

const CitiesGrid = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  margin-top: 3rem;
  gap: 2rem;
  
  @media (max-width: 768px) {
    gap: 1.5rem;
  }
`;

const CityCard = styled(Card)`
  height: 100%;
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease;
  cursor: pointer;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }
`;

const CityImage = styled(CardMedia)`
  height: 200px;
`;

const ViewMoreButton = styled(Button)`
  margin-top: 3rem;
  padding: 10px 24px;
  border-radius: 30px;
  font-weight: 500;
  text-transform: none;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
  }
`;

const AudioControls = styled(Box)`
  margin-top: 24px;
  padding: 16px;
  background-color: #f9f9f9;
  border-radius: 8px;
`;

const AudioButton = styled(Button)`
  margin: 0 8px;
  padding: 6px 16px;
  text-transform: none;
`;

interface Props {}

const CitiesPreviewSection: React.FC<Props> = () => {
  const { t, language } = useI18n();
  const navigate = useNavigate();
  const [selectedCity, setSelectedCity] = useState<City | null>(null);
  const [currentAudioLang, setCurrentAudioLang] = useState<'pt' | 'en' | 'es'>('pt');
  const [randomCities, setRandomCities] = useState<City[]>([]);
  
  useEffect(() => {
    // Seleciona 6 cidades aleatórias quando o componente é montado
    setRandomCities(getRandomCities(6));
  }, []);

  const handleCityClick = (city: City) => {
    setSelectedCity(city);
    setCurrentAudioLang(language as 'pt' | 'en' | 'es');
  };

  const handleCloseModal = () => {
    setSelectedCity(null);
  };

  const handleAudioLanguageChange = (lang: 'pt' | 'en' | 'es') => {
    setCurrentAudioLang(lang);
  };

  const handleViewAllClick = () => {
    navigate('/cidades');
  };

  return (
    <SectionContainer id="cities">
      <Container>
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <Box textAlign="center" sx={{ mb: 5 }}>
            <Typography variant="h2" color="primary" sx={{ position: 'relative', display: 'inline-block', mb: 4 }}>
              {t('cities_title')}
              <Box sx={{ position: 'absolute', bottom: -10, left: 0, width: '100%', height: 3, bgcolor: 'primary.main' }} />
            </Typography>
            
            <Typography variant="h6" align="center" sx={{ maxWidth: '800px', margin: '1.5rem auto 0', color: '#555' }}>
              {t('cities_subtitle')}
            </Typography>
          </Box>
          
          <Box sx={{ mb: 6 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              {randomCities.map((city, index) => (
                <Box key={city.id} sx={{ width: { xs: '100%', sm: 'calc(50% - 12px)', md: 'calc(33.333% - 16px)' } }}>
                  <motion.div
                    initial={{ y: 50, opacity: 0 }}
                    whileInView={{ y: 0, opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.1 * index }}
                  >
                    <CityCard onClick={() => handleCityClick(city)}>
                      <CityImage
                        image={city.image}
                        title={city.name}
                        sx={{ height: 180 }}
                      />
                      <CardContent sx={{ p: '16px', pb: '8px', flexGrow: 1, display: 'flex', flexDirection: 'column', position: 'relative', height: '200px' }}>
                        <Typography variant="h6" component="h3" gutterBottom color="primary" sx={{ fontWeight: 600 }}>
                          {city.name}
                        </Typography>
                        
                        <Typography variant="body2" color="textSecondary" sx={{ 
                          mb: 5, 
                          mt: 1, 
                          height: '80px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 4,
                          WebkitBoxOrient: 'vertical'
                        }}>
                          {city.description}
                        </Typography>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'absolute', bottom: 13, left: 16, right: 16 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              component="span"
                              sx={{
                                bgcolor: 'rgba(0, 77, 64, 0.12)',
                                color: 'rgba(0, 77, 64, 0.9)',
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                px: 1,
                                py: 0.4,
                                borderRadius: '4px',
                                display: 'inline-flex',
                                alignItems: 'center'
                              }}
                            >
                              <LocationOnIcon sx={{ fontSize: '0.75rem', mr: 0.5 }} />
                              {city.state}
                            </Box>
                            <Box 
                              component="span"
                              sx={{ 
                                bgcolor: 'rgba(97, 97, 97, 0.08)',
                                color: 'rgba(97, 97, 97, 0.9)',
                                fontSize: '0.75rem',
                                fontWeight: 500,
                                px: 1,
                                py: 0.4,
                                borderRadius: '4px',
                                display: 'inline-flex',
                                alignItems: 'center'
                              }}
                            >
                              {city.region === 'nordeste' ? 'Nordeste' : 
                               city.region === 'centro-oeste' ? 'Centro-Oeste' : 
                               city.region === 'norte' ? 'Norte' : 
                               city.region === 'sudeste' ? 'Sudeste' : 
                               city.region === 'sul' ? 'Sul' : city.region}
                            </Box>
                          </Box>
                          
                          <Button size="small" color="primary" sx={{ textTransform: 'none' }}>
                            Saiba mais →
                          </Button>
                        </Box>
                      </CardContent>
                    </CityCard>
                  </motion.div>
                </Box>
              ))}
            </Box>
          </Box>
          
          <Box sx={{ textAlign: 'center' }}>
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.6 }}
            >
              <ViewMoreButton
                variant="contained"
                color="primary"
                onClick={handleViewAllClick}
                startIcon={<MapIcon />}
              >
                {t('view_all_cities')}
              </ViewMoreButton>
            </motion.div>
          </Box>
        </motion.div>
      </Container>
      
      {/* Modal de detalhes da cidade */}
      <Dialog
        open={Boolean(selectedCity)}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
      >
        {selectedCity && (
          <>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h4" component="div">
                {selectedCity.name}
              </Typography>
              <IconButton edge="end" color="inherit" onClick={handleCloseModal} aria-label="close">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            
            <DialogContent dividers>
              <Box sx={{ mb: 3 }}>
                <img
                  src={selectedCity.image}
                  alt={selectedCity.name}
                  style={{ width: '100%', borderRadius: '8px', maxHeight: '400px', objectFit: 'cover' }}
                />
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <Box
                  component="span"
                  sx={{
                    bgcolor: 'rgba(0, 77, 64, 0.12)',
                    color: 'rgba(0, 77, 64, 0.9)',
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    px: 1.5,
                    py: 0.5,
                    borderRadius: '4px',
                    display: 'inline-flex',
                    alignItems: 'center'
                  }}
                >
                  <LocationOnIcon sx={{ fontSize: '0.85rem', mr: 0.5 }} />
                  {selectedCity.state}
                </Box>
                <Box 
                  component="span"
                  sx={{ 
                    bgcolor: 'rgba(97, 97, 97, 0.08)',
                    color: 'rgba(97, 97, 97, 0.9)',
                    fontSize: '0.85rem',
                    fontWeight: 500,
                    px: 1.5,
                    py: 0.5,
                    borderRadius: '4px',
                    display: 'inline-flex',
                    alignItems: 'center'
                  }}
                >
                  {selectedCity.region === 'nordeste' ? 'Nordeste' : 
                   selectedCity.region === 'centro-oeste' ? 'Centro-Oeste' : 
                   selectedCity.region === 'norte' ? 'Norte' : 
                   selectedCity.region === 'sudeste' ? 'Sudeste' : 
                   selectedCity.region === 'sul' ? 'Sul' : selectedCity.region}
                </Box>
              </Box>
              
              <Typography variant="h6" gutterBottom color="primary">
                {t('modal_history')}
              </Typography>
              <Typography variant="body1" paragraph>
                {selectedCity.history}
              </Typography>
              
              <Typography variant="h6" gutterBottom color="primary">
                {t('modal_culture')}
              </Typography>
              <Typography variant="body1" paragraph>
                {selectedCity.culture}
              </Typography>
              
              <Typography variant="h6" gutterBottom color="primary">
                {t('modal_attractions')}
              </Typography>
              <Typography variant="body1" paragraph>
                {selectedCity.attractions}
              </Typography>
              
              {selectedCity.hasAudio && (
                <Box mt={3}>
                  <Typography variant="h6" gutterBottom color="primary">
                    {t('modal_listen')}
                  </Typography>
                  
                  <AudioControls>
                    <Box sx={{ mb: 2 }}>
                      <AudioButton
                        variant={currentAudioLang === 'pt' ? 'contained' : 'outlined'}
                        color="primary"
                        onClick={() => handleAudioLanguageChange('pt')}
                      >
                        {t('audio_pt')}
                      </AudioButton>
                      <AudioButton
                        variant={currentAudioLang === 'en' ? 'contained' : 'outlined'}
                        color="primary"
                        onClick={() => handleAudioLanguageChange('en')}
                      >
                        {t('audio_en')}
                      </AudioButton>
                      <AudioButton
                        variant={currentAudioLang === 'es' ? 'contained' : 'outlined'}
                        color="primary"
                        onClick={() => handleAudioLanguageChange('es')}
                      >
                        {t('audio_es')}
                      </AudioButton>
                    </Box>
                    
                    <audio
                      controls
                      src={`/assets/audio/${selectedCity.id}_${currentAudioLang}.mp3`}
                      style={{ width: '100%' }}
                    >
                      Seu navegador não suporta o elemento de áudio.
                    </audio>
                  </AudioControls>
                </Box>
              )}
            </DialogContent>
            
            <DialogActions>
              <Button onClick={handleCloseModal} color="primary">
                Fechar
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </SectionContainer>
  );
};

export default CitiesPreviewSection;
